'use client';

import hardenReactMarkdown from 'harden-react-markdown';
import { useSyncExternalStore } from 'react';
import ReactMarkdown, { type Options } from 'react-markdown';

const HardenedReactMarkdown = hardenReactMarkdown(ReactMarkdown);

function subscribeNoop() {
  return () => {};
}

/** SSR / prerender fallback when public app URL is not configured (dev). */
function devLoopbackOrigin(): string {
  const octets = [127, 0, 0, 1];
  const port = 3000;
  return `http://${octets.join('.')}:${port}`;
}

function serverMarkdownOrigin(): string {
  const configured = process.env.NEXT_PUBLIC_APP_URL;
  if (configured) {
    try {
      return new URL(configured).origin;
    } catch {
      /* ignore */
    }
  }
  return devLoopbackOrigin();
}

function clientMarkdownOrigin(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return serverMarkdownOrigin();
}

/**
 * Resolves relative URLs in hardened markdown consistently across SSR and hydration.
 * Configure the app’s public URL in env so SSR matches the deployed origin.
 */
function useMarkdownDefaultOrigin(): string {
  return useSyncExternalStore(subscribeNoop, clientMarkdownOrigin, serverMarkdownOrigin);
}

export type SafeMarkdownProps = Options & {
  /**
   * Origin for resolving relative links/images (harden-react-markdown).
   * Defaults to client window.origin, else configured public URL, else loopback dev origin.
   */
  defaultOrigin?: string;
  allowedLinkPrefixes?: string[];
  allowedImagePrefixes?: string[];
  allowDataImages?: boolean;
  allowedProtocols?: string[];
};

/**
 * react-markdown wrapped with harden-react-markdown: blocks raw HTML (no rehype-raw),
 * dangerous URL schemes, and filters link/image targets. Passes through `components`,
 * `remarkPlugins`, and other react-markdown options. Defaults allow http(s) links and
 * images (`*`) while still blocking javascript:, data: in links, etc.
 */
export function SafeMarkdown(props: SafeMarkdownProps) {
  const {
    defaultOrigin: defaultOriginProp,
    allowedLinkPrefixes = ['*'],
    allowedImagePrefixes = ['*'],
    allowDataImages = false,
    allowedProtocols,
    ...markdownProps
  } = props;

  const syncedOrigin = useMarkdownDefaultOrigin();
  const defaultOrigin = defaultOriginProp ?? syncedOrigin;

  return (
    <HardenedReactMarkdown
      defaultOrigin={defaultOrigin}
      allowedLinkPrefixes={allowedLinkPrefixes}
      allowedImagePrefixes={allowedImagePrefixes}
      allowDataImages={allowDataImages}
      {...(allowedProtocols !== undefined ? { allowedProtocols } : {})}
      {...markdownProps}
    />
  );
}
